import { Link } from 'expo-router';
import { CheckCircle2Icon, Share2Icon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, View } from 'react-native';

import { useAnalytics } from '@/components/analytics-provider';
import { useCurrentTime } from '@/lib/dev-clock';
import { signInHref } from '@/lib/auth-return';
import {
  cancelEventRegistration,
  createEventRegistration,
  EventRegistrationApiError,
  fetchMyRegistration,
  type EventRegistration,
} from '@/lib/event-registration';
import { formatEventWhen, type Event } from '@/lib/events';
import { shareEventInvite } from '@/lib/share';
import { BRAND } from '@sixam/config/brand';
import {
  eventRegistrationEligibility,
  eventRegistrationUnavailableCopy,
  type EventRegistrationReason,
} from '@sixam/shared/event-registration-eligibility';
import { effectiveRegistrationClosesAt } from '@sixam/shared/event-time';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';

export type EventRegistrationPanelState =
  | Readonly<{ kind: 'loading' }>
  | Readonly<{ kind: 'eligible' }>
  | Readonly<{ kind: 'unavailable'; reason: EventRegistrationReason }>
  | Readonly<{ kind: 'error'; message: string }>
  | Readonly<{ kind: 'confirmed' }>;

// The render branches intentionally mirror the user-visible registration states.
// eslint-disable-next-line sonarjs/cognitive-complexity
export function EventRegistrationPanel({
  busy = false,
  event,
  onCancel,
  onRegister,
  onShare,
  state,
  timeZone,
  message,
}: Readonly<{
  busy?: boolean;
  event: Event;
  onCancel?: () => void;
  onRegister?: () => void;
  onShare?: () => void;
  state: EventRegistrationPanelState;
  timeZone?: string;
  message?: string | null;
}>) {
  if (state.kind === 'loading') {
    return (
      <View className="min-h-14 items-center justify-center rounded-xl border border-border bg-card p-4">
        <ActivityIndicator color={BRAND.color.orange} />
      </View>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <View className="rounded-xl border border-border bg-card p-4">
        <Text variant="muted">{eventRegistrationUnavailableCopy(state.reason)}</Text>
      </View>
    );
  }

  if (state.kind === 'error') {
    return (
      <View className="gap-3 rounded-xl border border-destructive bg-card p-4">
        <Text className="text-destructive" selectable>
          {state.message}
        </Text>
        {onRegister ? (
          <Button disabled={busy} onPress={onRegister}>
            <Text>{busy ? 'Trying again…' : 'Try again'}</Text>
          </Button>
        ) : null}
      </View>
    );
  }

  if (state.kind === 'eligible') {
    return (
      <Button disabled={busy} onPress={onRegister} size="lg">
        {busy ? <ActivityIndicator color={BRAND.color.white} /> : null}
        <Text>{busy ? 'Registering…' : 'Register'}</Text>
      </Button>
    );
  }

  return (
    <View className="gap-4 rounded-2xl border border-primary bg-primary/10 p-5">
      <View className="items-center gap-2">
        <Icon as={CheckCircle2Icon} className="size-16 text-primary" />
        <Text variant="h2" className="text-center">
          You’re coming
        </Text>
        <Text className="text-center font-bold">{event.title}</Text>
        <Text variant="muted" className="text-center">
          {formatEventWhen(event.starts_at, event.ends_at, timeZone)}
          {event.place_name ? ` · ${event.place_name}` : ''}
        </Text>
      </View>
      <Button disabled={busy} onPress={onShare} size="lg">
        <Icon as={Share2Icon} className="size-5 text-primary-foreground" />
        <Text>Invite friends</Text>
      </Button>
      {message ? (
        <Text className="text-center text-destructive" selectable>
          {message}
        </Text>
      ) : null}
      {onCancel ? (
        <Button disabled={busy} onPress={onCancel} variant="ghost">
          <Text>{busy ? 'Updating…' : 'Cancel registration'}</Text>
        </Button>
      ) : null}
    </View>
  );
}

export function EventRegistrationButton({
  event,
  onGoingChange,
  returnPath,
  tenantName,
  tenantSlug,
  timeZone,
  userId,
}: Readonly<{
  event: Event;
  onGoingChange: (isGoing: boolean) => void;
  returnPath: string;
  tenantName?: string | null;
  tenantSlug: string;
  timeZone?: string;
  userId: string | null;
}>) {
  const analytics = useAnalytics();
  const now = useCurrentTime();
  const [registration, setRegistration] = React.useState<EventRegistration | null>(null);
  const [isLoading, setIsLoading] = React.useState(Boolean(userId));
  const [isBusy, setIsBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!userId) {
      return;
    }

    let active = true;
    fetchMyRegistration(event.id)
      .then((nextRegistration) => {
        if (!active) return;
        setRegistration(nextRegistration);
        onGoingChange(nextRegistration?.status === 'confirmed');
        setError(null);
      })
      .catch((loadError: unknown) => {
        if (!active) return;
        setError(loadError instanceof Error ? loadError.message : 'Could not load registration.');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [event.id, onGoingChange, userId]);

  const confirmed = registration?.status === 'confirmed';
  const eligibility = eventRegistrationEligibility(event, now);
  let state: EventRegistrationPanelState;
  if (userId && isLoading) {
    state = { kind: 'loading' };
  } else if (confirmed) {
    state = { kind: 'confirmed' };
  } else if (error) {
    state = { kind: 'error', message: error };
  } else if (eligibility.eligible) {
    state = { kind: 'eligible' };
  } else {
    state = { kind: 'unavailable', reason: eligibility.reason };
  }

  async function register() {
    if (!userId) return;
    setIsBusy(true);
    setError(null);
    analytics.capture('event_registration_attempted', { event_id: event.id });
    try {
      const response = await createEventRegistration(event.id);
      setRegistration(response.registration);
      onGoingChange(true);
      analytics.capture('event_registration_succeeded', { event_id: event.id });
    } catch (registrationError: unknown) {
      const reason =
        registrationError instanceof EventRegistrationApiError
          ? registrationError.reason
          : 'unavailable';
      setError(
        registrationError instanceof Error
          ? registrationError.message
          : 'Could not register for this event.'
      );
      analytics.capture('event_registration_failed', { event_id: event.id, reason });
    } finally {
      setIsBusy(false);
    }
  }

  async function cancel() {
    setIsBusy(true);
    setError(null);
    try {
      const nextRegistration = await cancelEventRegistration(event.id);
      setRegistration(nextRegistration);
      onGoingChange(false);
      analytics.capture('event_registration_cancelled', { event_id: event.id });
    } catch (cancellationError: unknown) {
      setError(
        cancellationError instanceof Error
          ? cancellationError.message
          : 'Could not cancel registration.'
      );
      analytics.capture('event_registration_cancellation_failed', { event_id: event.id });
    } finally {
      setIsBusy(false);
    }
  }

  async function share() {
    try {
      await shareEventInvite({
        eventId: event.id,
        eventTitle: event.title,
        eventWhen: formatEventWhen(event.starts_at, event.ends_at, timeZone),
        placeName: event.place_name,
        tenantName,
        tenantSlug,
      });
      analytics.capture('event_registration_shared', { event_id: event.id });
    } catch {
      analytics.capture('event_registration_share_failed', { event_id: event.id });
    }
  }

  if (!userId && state.kind === 'eligible') {
    return (
      <Link href={signInHref(returnPath)} asChild>
        <Button size="lg">
          <Text>Sign in to register</Text>
        </Button>
      </Link>
    );
  }

  return (
    <EventRegistrationPanel
      busy={isBusy}
      event={event}
      message={confirmed ? error : null}
      onCancel={
        confirmed && now < effectiveRegistrationClosesAt(event) ? () => void cancel() : undefined
      }
      onRegister={() => void register()}
      onShare={() => void share()}
      state={state}
      timeZone={timeZone}
    />
  );
}
